import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PullToRefresh from 'react-simple-pull-to-refresh';
import * as timeUtil from '@chronos/time-util';
import useWorklogsList from '@chronos/ui-toolkit/hooks/useWorklogsList';
import Estimate from '@chronos/components/Estimate';
import Spinner from '@chronos/components/Spinner';
import WorklogsList from '@chronos/components/WorklogsList';
import TrackedProgress from '@chronos/components/TrackedProgress';
import LabelList from '@chronos/components/LabelList';
import {
  selectOnlineUsers,
  selectLiveWorklogsByType,
} from '@chronos/slices/boardSlice';
import {
  resetState,
  setProgressStats,
  selectProgressStats,
} from '@chronos/slices/cardSlice';
import { useGetEstimatesQuery } from '@chronos/slices/estimatesApi';
import {
  useGetUsersQuery,
  initialUserState,
  useTrelloMembers,
} from '@chronos/slices/usersApi';
import { getEstimatesWithUser } from '@chronos/utils';
import { getTotalEstimate, getUsersStatsMap } from '@chronos/time-util';
import eventEmitter from '@chronos/utils/eventEmitter';
import {
  initialCardState,
  useGetTrelloBoardCardsQuery,
  useTrelloCards,
} from '@chronos/slices/cardsApi';
import { useBoardContext } from '@chronos/ui-toolkit/contexts';
import useWorklogsFilter from '@chronos/ui-toolkit/hooks/useWorklogsFilter';
import { useSaveBoardMemberSettingsMutation } from '@chronos/slices/settingsApi';
import { selectUserSettings } from '../../slices/appSlice';
import { trelloCardFields } from '../../constants';
import EstimateModal from '../EstimateModal';
import {
  closeModal,
  openModal,
  selectModalState,
} from '../../slices/modalSlice';
import * as GLOBAL_S from '../../styled/styled-global';
import * as S from './styled';

export const mapState = (state) => ({
  progressStats: selectProgressStats(state),
  onlineUsers: selectOnlineUsers(state),
  userSettings: selectUserSettings(state),
  modalState: selectModalState(state),
});

const CardDetails = () => {
  const dispatch = useDispatch();
  const state = useSelector(mapState);
  const liveWorklogsCount = useRef(0);
  const [pullToRefreshCounter, setPullCounter] = useState(0);
  const { userSettings, modalState } = state;
  const boardLiveWorklogs = useSelector((s) =>
    selectLiveWorklogsByType(s, { roomType: 'board' })
  );
  const boardContext = useBoardContext();
  const { memberId, boardId, organizationId } = boardContext;

  const { cardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const cardLiveWorklogs = useMemo(() => {
    return boardLiveWorklogs.filter((w) => w.cardId === cardId);
  }, [boardLiveWorklogs, cardId]);

  const {
    data: { ids: usersIds, entities: usersEntities } = initialUserState,
    isSuccess: isUsersLoaded,
  } = useGetUsersQuery({
    boardId,
    organizationId,
  });

  const [saveSettings] = useSaveBoardMemberSettingsMutation();

  const {
    isLoading: isCardsLoading,
    data: { ids: cardsIds, entities: cardsEntities } = initialCardState,
  } = useGetTrelloBoardCardsQuery({
    boardId,
    filter: 'visible',
    fields: trelloCardFields,
  });
  const card = cardsEntities[cardId];
  const [filter, { onChangeFilter, requestParams }] = useWorklogsFilter({
    cardId,
    boardId,
    memberId,
    isAllowToSeeOthers:
      boardContext.boardMemberPermissions?.isAllowToSeeOthers || false,
    weekStartsOn: boardContext.boardAdminSettings?.weekStartsOn || 0,
    usersIds,
    usersEntities,
    cardsIds,
    cardsEntities,
  });
  const [fetchOutTrelloCards] = useTrelloCards({
    boardId,
    filter: 'visible',
    fields: trelloCardFields,
  });
  const [fetchOutTrelloMembers] = useTrelloMembers({
    boardId,
    organizationId: boardContext.belongToOrganization
      ? boardContext.organizationId
      : null,
  });
  const [
    { state: worklogsState, listRef, infiniteLoaderRef, autosizerRef },
    {
      fetchWorklogs,
      fetchWorklogsMeta,
      reloadList,
      clearList,
      addNewDummyWorklog,
      isItemLoaded,
      deleteWorklog,
      onShowScreenshots,
    },
  ] = useWorklogsList({
    memberId,
    cardId,
    requestParams,
    filter,
    usersEntities,
    fetchOutTrelloMembers,
    cardsEntities,
    fetchOutTrelloCards,
    liveWorklogs: boardLiveWorklogs,
    showLiveWorklogs:
      boardContext.boardMemberSettings?.showLiveWorklogs || false,
    showOwnLiveWorklogs:
      boardContext.boardMemberSettings?.showOwnLiveWorklogs || false,
    allowedToSeeOthersWorklog:
      boardContext.boardMemberPermissions?.isAllowToSeeOthers || false,
    weekStartsOn: boardContext.boardAdminSettings?.weekStartsOn,
    defaultWorklogsCount: 15,
  });

  const selectedMembers = filter.members.selected;
  const selectedUserIds = useMemo(
    () => selectedMembers.map((u) => u.value),
    [selectedMembers]
  );

  const { data: estimates = [], isLoading: isEstimatesLoading } =
    useGetEstimatesQuery({
      cardIds: [cardId],
    });

  const userStatsMap = useMemo(
    () =>
      getUsersStatsMap(
        usersIds,
        usersEntities,
        worklogsState.userSeconds,
        estimates
      ),
    [usersEntities, worklogsState.userSeconds, estimates]
  );

  const usersStats = useMemo(
    () =>
      Object.values(userStatsMap).filter(
        (userStat) => userStat.loggedSeconds && !userStat.estimatedSeconds
      ),
    [userStatsMap]
  );

  const totalEstimate = useMemo(() => getTotalEstimate(estimates), [estimates]);

  const currentEstimate = useMemo(() => {
    if (!selectedUserIds.length) return totalEstimate;
    return selectedUserIds.reduce(
      (acc, userId) => acc + userStatsMap[userId].estimatedSeconds,
      0
    );
  }, [userStatsMap, selectedUserIds, totalEstimate]);

  const estimatesWithUser = useMemo(
    () => (isUsersLoaded ? getEstimatesWithUser(estimates, usersEntities) : []),
    [isUsersLoaded, estimates, usersEntities]
  );

  const handleSavedWorklog = useCallback(async () => {
    await fetchWorklogsMeta({
      shouldMarkAsLoading: false,
      shouldDelayBeforePerform: false,
      shouldReloadListIfChange: false,
    });
    await reloadList();
  }, [dispatch, fetchWorklogsMeta]);

  const handleSavingWorklog = useCallback(
    async ({ values, mode }) => {
      if (mode === 'addWorklog' && values.cardId === cardId) {
        addNewDummyWorklog();
      }
    },
    [cardId, addNewDummyWorklog]
  );

  useEffect(() => {
    eventEmitter.on('savingWorklog', handleSavingWorklog);
    eventEmitter.on('savedWorklog', handleSavedWorklog);
    return () => {
      eventEmitter.off('savingWorklog', handleSavingWorklog);
      eventEmitter.off('savedWorklog', handleSavedWorklog);
    };
  }, [handleSavingWorklog, handleSavedWorklog]);

  useEffect(() => {
    eventEmitter.on('updateState', fetchWorklogsMeta);
    return () => {
      eventEmitter.off('updateState', fetchWorklogsMeta);
    };
  }, [fetchWorklogsMeta]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, []);

  useEffect(() => {
    dispatch(
      setProgressStats(
        timeUtil.calculateTimeProgress({
          estimate: currentEstimate,
          trackedTime: worklogsState.totalTime,
        })
      )
    );
  }, [worklogsState.totalTime, currentEstimate]);

  useEffect(() => {
    fetchWorklogsMeta();
  }, [cardId]);

  useEffect(() => {
    /*
     * Only if worklogs length become less than before means most probably a new worklog
     * has been appeared and it makes sense run fetchWorklogsMeta()
     */
    const becomeLess = liveWorklogsCount.current > cardLiveWorklogs.length;
    if (becomeLess) {
      liveWorklogsCount.current = cardLiveWorklogs.length;
      fetchWorklogsMeta();
    } else {
      liveWorklogsCount.current = cardLiveWorklogs.length;
    }
  }, [cardLiveWorklogs.length]);

  const onHideOwnLiveWorklog = useCallback(
    () =>
      saveSettings({
        body: {
          boardId,
          showOwnLiveWorklogs: false,
        },
      }),
    [boardId]
  );

  const worklogsCount = worklogsState.listItemsCount;
  const worklogsListProps = {
    worklogs: worklogsState.worklogs,
    listItems: worklogsState.listItems,
    dayPoints: worklogsState.dayPoints,
    weekPoints: worklogsState.weekPoints,
    cards: cardsEntities,
    users: usersEntities,
    onlineUsers: state.onlineUsers,
    worklogsCount,
    totalTime: worklogsState.totalTimeStr,
    loadMoreWorklogs: fetchWorklogs,
    isItemLoaded,
    infiniteLoaderRef,
    listRef,
    autosizerRef,
    showWorklogButtonsDefault: false,
    shouldDisplayDivider: false,
    shouldDisplayCard: false,
    isWorklogsListHidden: userSettings.isWorklogsListHidden ?? false,
    isShowScreenshotsBtn: true,
    isShowEditBtn: boardContext.boardMemberPermissions?.allowEdit ?? false,
    isShowDeleteBtn: boardContext.boardMemberPermissions?.allowDelete ?? false,
    showStartBtn: false,
    isMobile: true,
    popoverPlacement: 'below',
    commentPlacement: 'body',
    onAddWorklog: () => {
      dispatch(
        openModal({
          name: 'WorklogFormModal',
          meta: { title: 'Add worklog', mode: 'addWorklog' },
        })
      );
    },
    onEditWorklog: (worklog) => {
      const meta = {
        title: 'Edit worklog',
        mode: 'editWorklog',
        cardId: worklog.cardId,
        worklog: {
          id: worklog.id,
          startedAt: worklog.startedAt.toString(),
          accountId: worklog.accountId,
          seconds: worklog.seconds,
          comment: worklog.comment,
        },
      };
      dispatch(openModal({ name: 'WorklogFormModal', meta }));
    },
    onHideOwnLiveWorklog,
    onDeleteWorklog: async (worklog, index) => {
      await deleteWorklog(worklog, index);
      await fetchWorklogsMeta({
        shouldMarkAsLoading: false,
        shouldDelayBeforePerform: false,
        shouldReloadListIfChange: false,
      });
    },
    onClickCardname: (item) => {
      if (item?.id) {
        navigate(`cards/${item.id}`, {
          state: { backgroundLocation: location },
        });
      }
    },
    onShowScreenshots,
  };

  const handleUserClick = useCallback(
    (userId) => {
      if (selectedUserIds.includes(userId)) {
        onChangeFilter(
          'members',
          selectedMembers.filter(({ value }) => value !== userId)
        );
      } else {
        onChangeFilter('members', selectedMembers.concat({ value: userId }));
      }
    },
    [selectedUserIds, selectedMembers]
  );

  const closeWorklogFormModal = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const handleEstimateEdit = useCallback(() => {
    dispatch(openModal({ name: 'EstimateModal' }));
  }, []);

  const users = useMemo(() => Object.values(usersEntities), [usersEntities]);

  if (isCardsLoading) {
    return <Spinner size="large" />;
  }

  const totalEstimateString = timeUtil.stj(totalEstimate);
  return (
    <>
      <S.CardName>{card.name}</S.CardName>
      <S.Labels>
        <S.Sublitle>Label</S.Sublitle>
        <LabelList
          isLarge
          isExpanded
          labels={card.labels}
        />
      </S.Labels>

      {card.desc && (
        <S.CardDescription>
          <span>{card.desc}</span>
        </S.CardDescription>
      )}

      <S.TrackedProgressWrapper>
        <Estimate
          isLoading={isEstimatesLoading}
          estimates={estimatesWithUser}
          onEstimateEdit={handleEstimateEdit}
          totalEstimateString={totalEstimateString}
          onUserClick={handleUserClick}
          userStatsMap={userStatsMap}
          selectedUserIds={selectedUserIds}
          isMobile
        />
        <TrackedProgress
          onUserClick={handleUserClick}
          stats={state.progressStats}
          usersStats={usersStats}
          showAvatarsIfEmpty={false}
          selectedUserIds={selectedUserIds}
        />
      </S.TrackedProgressWrapper>

      <PullToRefresh
        refreshingContent={<Spinner />}
        pullingContent={null}
        onRefresh={async () => {
          setPullCounter(pullToRefreshCounter + 1);
          clearList();
          await fetchWorklogsMeta({
            shouldMarkAsLoading: false,
            shouldDelayBeforePerform: false,
            shouldReloadListIfChange: false,
          });
        }}
      >
        {worklogsCount === 0 ? (
          <S.NotFoundMessage>{`Card doesn't have worklogs`}</S.NotFoundMessage>
        ) : (
          <GLOBAL_S.WorklogsListContainer>
            <WorklogsList
              showTimeline
              {...worklogsListProps}
            />
          </GLOBAL_S.WorklogsListContainer>
        )}
      </PullToRefresh>
      {modalState.isOpen && modalState.name === 'EstimateModal' && (
        <EstimateModal
          isOpen
          handleClose={closeWorklogFormModal}
          users={users}
          organizationId={organizationId}
          boardId={boardId}
          cardId={cardId}
          {...modalState.meta}
        />
      )}
    </>
  );
};

export default CardDetails;
